import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { PageLayout } from "@/components/site/PageLayout";
import {
  FeatureGrid,
  DeliverablesList,
  ServiceCta,
} from "@/components/site/ServiceBlocks";
import { FaqSection } from "@/components/site/Faq";
import { ldScript, serviceSchema, faqSchema } from "@/lib/schema";
import svcPv from "@/assets/hero-pv.jpg";
import pvDeliverablesAsset from "@/assets/solar-roof-aerial.webp.asset.json";
import pvProcessAsset from "@/assets/man2.jpg.asset.json";
const pvDeliverables = pvDeliverablesAsset.url;
const pvProcess = pvProcessAsset.url;

const FAQS = [
  {
    q: "What's included in a PV plan set?",
    a: "A full sealed permit set: site plan, roof plan, single-line diagram, three-line diagram, string layouts, structural attachment details, equipment specs, and any AHJ-specific inserts required to hit approval on the first review.",
  },
  {
    q: "How fast can you turn around a residential PV design?",
    a: "Straightforward residential PV designs are typically ready within 48 to 72 hours of receiving site info. Commercial and utility-scale timelines are scoped up front based on system size and complexity.",
  },
  {
    q: "Which states can you stamp in?",
    a: "We stamp and seal nationwide and in the Caribbean. We maintain a licensed engineer for every jurisdiction we deliver into.",
  },
  {
    q: "Do you handle interconnection and utility paperwork?",
    a: "We deliver the engineered plan set and single-line drawings that interconnection applications require. We can coordinate directly with your utility contact when needed.",
  },
  {
    q: "Can you re-engineer a project another firm designed?",
    a: "Yes. We regularly pick up projects mid-stream, review the existing scope, and re-engineer the plan set so it clears the AHJ without further revision cycles.",
  },
];

export const Route = createFileRoute("/pv")({
  head: () => ({
    ...pageHead({
      title: "Solar PV Design & Stamped Plans · SunSmart Engineering",
      description:
        "Solar PV system design and sealed plan sets for residential and commercial projects, prepared for fast permitting and code-compliant installation.",
      path: "/pv",
    }),
    scripts: [
      ldScript(
        serviceSchema({
          name: "Solar PV System Design",
          description:
            "End-to-end photovoltaic system design and stamped, sealed plan sets for residential, commercial, and utility-scale projects.",
          url: "/pv",
          serviceType: "Solar PV Engineering",
        }),
      ),
      ldScript(faqSchema(FAQS)),
    ],
  }),
  component: PvPage,
});

function PvPage() {
  return (
    <PageLayout
      kicker="Renewables & Resiliency · Solar Electric"
      title="PV System Design Services"
      intro="We provide end-to-end photovoltaic (PV) system design services tailored to meet the needs of contractors and solar professionals. From initial assessments to delivering code-compliant, stamped, and sealed plans, we help streamline the process so you can focus on successful installations. Our team has designed projects of every size and complexity, so no matter the challenge, we have what it takes to get it done right."
    >
      <FeatureGrid
        label="01"
        title={
          <>
            More than <span className="text-sun">just plan sets.</span>
          </>
        }
        items={[
          { title: "Site Analysis", description: "We evaluate site conditions, including roof space, shading, and structural considerations, to ensure optimal system placement and performance." },
          { title: "Feasibility Studies", description: "Our detailed assessments determine project viability, identifying any challenges and solutions before you start." },
          { title: "System Sizing and Layout", description: "Using advanced modeling tools, we create precise designs that maximize energy production while adhering to local regulations and safety standards." },
          { title: "Permitting and Stamped Engineering Plans", description: "We provide stamped and sealed engineering documents to help you meet permitting requirements with ease." },
        ]}
      />

      <DeliverablesList
        label="02"
        title={
          <>
            Why choose <span className="text-sun">SunSmart Engineering?</span>
          </>
        }
        intro="We understand the importance of dependable, accurate designs in driving the success of your solar projects. Our team works closely with you to ensure every detail of your PV system is covered, delivering results that exceed expectations."
        image={pvDeliverables}
        imageAlt="Technicians installing photovoltaic solar panels on a rooftop"
        items={[
          { title: "Efficient and Cost-Effective Designs", description: "We prioritize efficiency and cost-effectiveness to deliver systems that balance performance and budget." },
          { title: "Customized Solutions", description: "Every design is tailored to your project's unique specifications, whether residential, commercial, or industrial." },
          { title: "Regulation and Safety Compliance", description: "All designs meet NEC standards, local building codes, and safety requirements for worry-free permitting." },
          { title: "Ongoing Monitoring & Support", description: "While our primary focus is on design, we can offer guidance and recommendations to help ensure system success post-installation." },
        ]}
      />

      <FaqSection items={FAQS} title={<>PV design <span className="text-sun">questions.</span></>} />

      <ServiceCta
        label="03"
        title={
          <>
            Your partner for <span className="text-sun">seamless success.</span>
          </>
        }
        intro="We understand the importance of dependable, accurate designs in driving the success of your solar projects. Our team works closely with you to ensure every detail of your PV system is covered."
        image={pvProcess}
        imageAlt="Engineer reviewing a solar PV design layout on screen"
      />
    </PageLayout>
  );
}
